import React, { useMemo } from 'react';
import { useMailProvider } from './MailProviderContext';
import { Inbox, Star, Send, FileText, Mail, Tag, PenSquare, Folder } from 'lucide-react';
import { FLUSIA_GRADIENT } from './MailThreadListItem';

interface MailNavMenuProps {
  organizationId: string;
  userId: string;
  onClose: () => void;
}

export function MailNavMenu({ organizationId, userId, onClose }: MailNavMenuProps) {
  const { useStore } = useMailProvider();
  const { activeLabel, setActiveLabel, loadThreads, setComposing, labels, drafts, unreadCount } = useStore();
  const userLabels = useMemo(() => Object.values(labels).filter(l => l.type === 'user'), [labels]);
  // Cartelle custom Outlook (type 'folder'): sezione dedicata, presente solo per Outlook
  // (Gmail non produce mai label 'folder' → sezione mai renderizzata, zero impatto).
  const folderLabels = useMemo(() => Object.values(labels).filter(l => l.type === 'folder'), [labels]);
  const draftCount = useMemo(() => Object.keys(drafts).length, [drafts]);

  const go = (id: string) => {
    setActiveLabel(id);
    loadThreads(organizationId, userId, id);
    onClose();
  };

  const items = [
    { id: 'INBOX', label: 'Posta in arrivo', icon: Inbox, badge: unreadCount || undefined },
    { id: 'STARRED', label: 'Speciali', icon: Star },
    { id: 'SENT', label: 'Inviati', icon: Send },
    { id: 'DRAFT', label: 'Bozze', icon: FileText, badge: draftCount || undefined },
    { id: 'ALL', label: 'Tutti', icon: Mail },
  ];

  return (
    <div className="w-56 flex-shrink-0 h-full flex flex-col bg-[#FBFBFA] dark:bg-gray-950 border-r border-[rgba(55,53,47,0.09)] dark:border-gray-800 overflow-y-auto">
      {/* Scrivi */}
      <div className="p-3">
        <button
          onClick={() => { setComposing(true); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-medium shadow-sm hover:opacity-90 transition-opacity"
          style={{ background: FLUSIA_GRADIENT }}
        >
          <PenSquare className="h-4 w-4" /> Scrivi
        </button>
      </div>

      {/* Voci principali */}
      <nav className="px-2 space-y-0.5">
        {items.map(({ id, label, icon: Icon, badge }) => {
          const active = activeLabel === id;
          return (
            <button
              key={id}
              onClick={() => go(id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-r-full text-sm transition-colors ${
                active ? 'bg-[#2383E2]/12 text-[#2383E2] font-semibold' : 'text-[#37352F] dark:text-white/80 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left truncate">{label}</span>
              {badge !== undefined && <span className="text-xs">{badge}</span>}
            </button>
          );
        })}
      </nav>

      {/* Etichette */}
      {userLabels.length > 0 && (
        <div className="mt-3 px-2">
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#787774] dark:text-white/40">Etichette</div>
          <div className="space-y-0.5">
            {userLabels.map(l => {
              const active = activeLabel === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-r-full text-sm transition-colors ${
                    active ? 'bg-[#2383E2]/12 text-[#2383E2] font-medium' : 'text-[#37352F] dark:text-white/80 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
                  }`}
                >
                  {l.backgroundColor
                    ? <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: l.backgroundColor }} />
                    : <Tag className="h-3.5 w-3.5 flex-shrink-0 text-[#787774]" />}
                  <span className="flex-1 text-left truncate">{l.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cartelle (solo Outlook) */}
      {folderLabels.length > 0 && (
        <div className="mt-3 px-2 pb-3">
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#787774] dark:text-white/40">Cartelle</div>
          <div className="space-y-0.5">
            {folderLabels.map(l => {
              const active = activeLabel === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-r-full text-sm transition-colors ${
                    active ? 'bg-[#2383E2]/12 text-[#2383E2] font-medium' : 'text-[#37352F] dark:text-white/80 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
                  }`}
                >
                  <Folder className="h-3.5 w-3.5 flex-shrink-0 text-[#787774]" />
                  <span className="flex-1 text-left truncate">{l.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
